import React, { useState, useEffect, useCallback } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { formatDate } from '../../utils/Utils';
import PipGoalsEditor from './PipGoalsEditor';
import { PIP_STATUS_COLORS, StatusBadge } from './config';

// P1 Görev Paketi A — PIP detay drawer'ı (İK): hedef editörü, check-in
// listesi + ekleme, başlat/kapat/iptal aksiyonları (ConfirmDialog'lu).
// Kabuk GenericFormDrawer'ın react-modern-drawer kurulumuyla birebir aynı
// (o bileşen şema-tabanlı olduğu için burada özel gövdeyle yeniden kurulur).
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
`;

const MetaLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 150px;
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.text.dark?.little || theme.colors.text.primary};
`;

const SectionTitle = styled.h4`
  margin: 20px 0 12px;
  padding-top: 16px;
  border-top: 1px solid #E8ECF0;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GoalCard = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.surface?.border || '#E8ECF0'};
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  font-size: 13px;
`;

const CheckInCard = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
  font-size: 13px;
`;

const CheckInMeta = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 12px;
  margin-top: 4px;
`;

const AchievedBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => (p.$achieved ? '#16A34A' : '#DC2626')};
  background: ${(p) => (p.$achieved ? '#DCFCE7' : '#FEF2F2')};
`;

const Loading = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
`;

// Kapanış modalı drawer'ın ÜZERİNDE açılır (DevelopmentPlansPage Modal deseni).
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 560px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

export default function PipDetailDrawer({
  pipId, open, onClose, canManage, pipApi, onChanged,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // usePipApi'nin döndürdüğü OBJE her render'da yeni — effect bağımlılıklarında
  // obje yerine useCallback'li stabil fonksiyonlar kullanılır (gereksiz yeniden
  // fetch önlenir).
  const {
    getById, update, start, cancel, addCheckIn, close,
  } = pipApi;

  const [pip, setPip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hedef editörü (taslak/aktif) — kaydedilene dek lokal state.
  const [editGoals, setEditGoals] = useState([]);
  const [goalsDirty, setGoalsDirty] = useState(false);

  const [checkInNote, setCheckInNote] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // 'start' | 'cancel'
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeOutcome, setCloseOutcome] = useState('successful');
  const [closureNote, setClosureNote] = useState('');
  const [goalMarks, setGoalMarks] = useState({}); // goalId -> bool

  const reload = useCallback(async () => {
    if (!pipId) return;
    setLoading(true);
    const detail = await getById(pipId);
    setPip(detail || null);
    setEditGoals((detail?.goals || []).map((g) => ({ title: g.title, successCriteria: g.successCriteria || '' })));
    setGoalsDirty(false);
    setLoading(false);
  }, [pipId, getById]);

  useEffect(() => {
    if (open && pipId) {
      setCheckInNote('');
      setCloseModalOpen(false);
      setConfirmAction(null);
      reload();
    }
  }, [open, pipId, reload]);

  const isEditable = canManage && (pip?.status === 'draft' || pip?.status === 'active');
  const statusCfg = pip ? (PIP_STATUS_COLORS[pip.status] || PIP_STATUS_COLORS.draft) : null;

  const notifyChanged = useCallback(() => {
    onChanged?.();
    reload();
  }, [onChanged, reload]);

  const handleSaveGoals = useCallback(async () => {
    if (!pip) return;
    setBusy(true);
    const ok = await update(pip.id, {
      goals: editGoals
        .filter((g) => g.title.trim())
        .map((g) => ({ title: g.title.trim(), successCriteria: g.successCriteria?.trim() || undefined })),
    });
    setBusy(false);
    if (ok) notifyChanged();
  }, [pip, editGoals, update, notifyChanged]);

  const handleConfirmAction = useCallback(async () => {
    if (!pip || !confirmAction) return;
    setBusy(true);
    const ok = confirmAction === 'start'
      ? await start(pip.id)
      : await cancel(pip.id);
    setBusy(false);
    setConfirmAction(null);
    if (ok) notifyChanged();
  }, [pip, confirmAction, start, cancel, notifyChanged]);

  const handleAddCheckIn = useCallback(async () => {
    if (!pip || !checkInNote.trim()) return;
    setBusy(true);
    const ok = await addCheckIn(pip.id, checkInNote.trim());
    setBusy(false);
    if (ok) {
      setCheckInNote('');
      notifyChanged();
    }
  }, [pip, checkInNote, addCheckIn, notifyChanged]);

  const openCloseModal = useCallback(() => {
    const marks = {};
    (pip?.goals || []).forEach((g) => { marks[g.id] = g.isAchieved === true; });
    setGoalMarks(marks);
    setCloseOutcome('successful');
    setClosureNote('');
    setCloseModalOpen(true);
  }, [pip]);

  const handleCloseConfirm = useCallback(async () => {
    if (!pip || !closureNote.trim()) return;
    setBusy(true);
    const ok = await close(pip.id, {
      outcome: closeOutcome,
      closureNote: closureNote.trim(),
      goals: (pip.goals || []).map((g) => ({ goalId: g.id, isAchieved: goalMarks[g.id] === true })),
    });
    setBusy(false);
    if (ok) {
      setCloseModalOpen(false);
      notifyChanged();
    }
  }, [pip, closeOutcome, closureNote, goalMarks, close, notifyChanged]);

  const outcomeOptions = [
    { id: 'successful', name: t('performance.pip.outcome.successful') },
    { id: 'unsuccessful', name: t('performance.pip.outcome.unsuccessful') },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="45%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: 380,
        margin: '12px',
        borderRadius: '6px',
        height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title>{t('performance.pip.detailTitle')}</Title>
        <CloseBtn onClick={onClose} aria-label={t('common.close')}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        {loading && <Loading>{t('common.loading')}...</Loading>}

        {!loading && pip && (
          <>
            <MetaRow>
              <MetaLabel>{t('performance.pip.employee')}:</MetaLabel>
              <MetaValue>{pip.employeeName || '-'}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('performance.pip.statusLabel')}:</MetaLabel>
              {statusCfg && (
                <StatusBadge color={statusCfg.color} bg={statusCfg.bg}>
                  {t(`performance.pip.status.${pip.status}`, { defaultValue: pip.status })}
                </StatusBadge>
              )}
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('performance.pip.durationLabel')}:</MetaLabel>
              <MetaValue>{pip.durationDays} {t('performance.pip.daysSuffix')}</MetaValue>
            </MetaRow>
            {pip.sourcePeriodName && (
              <MetaRow>
                <MetaLabel>{t('performance.pip.sourcePeriod')}:</MetaLabel>
                <MetaValue>{pip.sourcePeriodName}</MetaValue>
              </MetaRow>
            )}
            {pip.startedAt && (
              <MetaRow>
                <MetaLabel>{t('performance.pip.startedAt')}:</MetaLabel>
                <MetaValue>{formatDate(pip.startedAt, lang)}</MetaValue>
              </MetaRow>
            )}
            {pip.dueDate && (
              <MetaRow>
                <MetaLabel>{t('performance.pip.dueDate')}:</MetaLabel>
                <MetaValue>{formatDate(pip.dueDate, lang)}</MetaValue>
              </MetaRow>
            )}
            {pip.closedAt && (
              <MetaRow>
                <MetaLabel>{t('performance.pip.closedAt')}:</MetaLabel>
                <MetaValue>{formatDate(pip.closedAt, lang)}</MetaValue>
              </MetaRow>
            )}
            {pip.closureNote && (
              <MetaRow>
                <MetaLabel>{t('performance.pip.closureNote')}:</MetaLabel>
                <MetaValue>{pip.closureNote}</MetaValue>
              </MetaRow>
            )}

            <SectionTitle>{t('performance.pip.goals')}</SectionTitle>
            {isEditable ? (
              <>
                <PipGoalsEditor
                  goals={editGoals}
                  onChange={(next) => { setEditGoals(next); setGoalsDirty(true); }}
                />
                {goalsDirty && (
                  <div style={{ marginTop: 12 }}>
                    <Button onClick={handleSaveGoals} disabled={busy}>
                      {t('performance.pip.saveGoals')}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {(pip.goals || []).length === 0 && (
                  <MetaValue>{t('performance.pip.noGoals')}</MetaValue>
                )}
                {(pip.goals || []).map((g) => (
                  <GoalCard key={g.id}>
                    <strong>{g.title}</strong>
                    {g.isAchieved != null && (
                      <AchievedBadge $achieved={g.isAchieved}>
                        {g.isAchieved ? t('performance.pip.goalAchieved') : t('performance.pip.goalNotAchieved')}
                      </AchievedBadge>
                    )}
                    {g.successCriteria && (
                      <div style={{ marginTop: 4 }}>{g.successCriteria}</div>
                    )}
                    {g.note && (
                      <CheckInMeta>{g.note}</CheckInMeta>
                    )}
                  </GoalCard>
                ))}
              </>
            )}

            <SectionTitle>{t('performance.pip.checkIns')}</SectionTitle>
            {(pip.checkIns || []).length === 0 && (
              <MetaValue>{t('performance.pip.noCheckIns')}</MetaValue>
            )}
            {(pip.checkIns || []).map((c) => (
              <CheckInCard key={c.id}>
                <div>{c.note}</div>
                <CheckInMeta>{formatDate(c.createDate, lang)}</CheckInMeta>
              </CheckInCard>
            ))}
            {canManage && pip.status === 'active' && (
              <div style={{ marginTop: 12 }}>
                <TextInput
                  label={t('performance.pip.checkInNote')}
                  type="textarea"
                  rows={2}
                  value={checkInNote}
                  onChange={(e) => setCheckInNote(e.target.value)}
                />
                <div style={{ marginTop: 8 }}>
                  <Button variant="outline" onClick={handleAddCheckIn} disabled={busy || !checkInNote.trim()}>
                    {t('performance.pip.addCheckIn')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Body>

      {canManage && pip && (pip.status === 'draft' || pip.status === 'active') && (
        <Footer>
          {(pip.status === 'draft' || pip.status === 'active') && (
            <Button variant="outline" onClick={() => setConfirmAction('cancel')} disabled={busy}>
              {t('performance.pip.cancelPip')}
            </Button>
          )}
          {pip.status === 'draft' && (
            <Button
              onClick={() => setConfirmAction('start')}
              disabled={busy || (pip.goals || []).length === 0 || goalsDirty}
            >
              {t('performance.pip.startPip')}
            </Button>
          )}
          {pip.status === 'active' && (
            <Button onClick={openCloseModal} disabled={busy}>
              {t('performance.pip.closePip')}
            </Button>
          )}
        </Footer>
      )}

      <ConfirmDialog
        isOpen={confirmAction === 'start'}
        title={t('performance.pip.startPip')}
        message={t('performance.pip.startConfirm')}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        loading={busy}
      />
      <ConfirmDialog
        isOpen={confirmAction === 'cancel'}
        title={t('performance.pip.cancelPip')}
        message={t('performance.pip.cancelConfirm')}
        type="danger"
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        loading={busy}
      />

      {/* Kapanış modalı: sonuç + kapanış notu + hedef işaretleri */}
      <Modal $open={closeModalOpen}>
        {closeModalOpen && pip && (
          <ModalCard>
            <DialogTitle>{t('performance.pip.closePip')} — {pip.employeeName}</DialogTitle>
            <FieldGroup>
              <GenericSelect
                label={t('performance.pip.outcomeLabel')}
                options={outcomeOptions}
                value={closeOutcome}
                onChange={setCloseOutcome}
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.pip.closureNote')}
                type="textarea"
                rows={3}
                value={closureNote}
                onChange={(e) => setClosureNote(e.target.value)}
                required
              />
            </FieldGroup>
            <FieldGroup>
              {(pip.goals || []).map((g) => (
                <div key={g.id} style={{ marginBottom: 8 }}>
                  <CheckboxInput
                    label={`${g.title} — ${t('performance.pip.goalAchieved')}`}
                    checked={goalMarks[g.id] === true}
                    onChange={(checked) => setGoalMarks((prev) => ({ ...prev, [g.id]: checked }))}
                  />
                </div>
              ))}
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setCloseModalOpen(false)} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleCloseConfirm} disabled={busy || !closureNote.trim()}>
                {t('common.confirm')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>
    </Drawer>
  );
}
